import React from 'react';
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import { breakpoints } from '@/layouts/breakpoints'

const StyledComp = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: monospace;
  position: relative;

  .overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
    background: rgba(0, 0, 0, 0.85); /* Fallback if --blackr not defined */
    background: rgba(var(--blackr, 0, 0, 0), 0.9);
  }

  .bg-img {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -2;
    background-image: url(${props => props.$bgImage});
    background-size: cover;
    background-position: center;
  }

  /* Mobile First: Stack vertically */
  .left-section {
    flex: 1;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    width: calc(100% - 80px);
  }

  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: clamp(16px, 5vh, 40px) 0;
  }

  .title {
    font-size: clamp(28px, min(9vw, 9vh), 72px);
    font-weight: 300;
    color: white;
    line-height: 1.1;
    margin-bottom: clamp(12px, 3vh, 24px);
    letter-spacing: 2px;
    word-wrap: break-word;
  }

  .prompt {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(16px, min(5vw, 5vh), 32px);
    color: white;
    margin-bottom: clamp(16px, 4vh, 32px);
    font-family: monospace;
  }

  .cursor {
    display: inline-block;
    width: clamp(10px, min(3vw, 3vh), 20px);
    height: 3px;
    background: white;
    animation: blink 1s infinite;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  .subtitle {
    font-size: clamp(12px, min(2.5vw, 2.5vh), 16px);
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.6;
    width: 100%;
    max-width: 540px;
  }

  .cta-button {
    background: white;
    color: #1a1a1a;
    border: none;
    padding: clamp(10px, 2vh, 16px) 28px;
    border-radius: 8px;
    font-size: clamp(13px, min(2vw, 2vh), 16px);
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    margin-top: clamp(16px, 4vh, 32px);
    white-space: nowrap;
    touch-action: manipulation;

    svg {
      transition: transform 0.2s;
    }

    &:hover {
      background: #f0f0f0;
      transform: translateY(-2px);
      svg {
        transform: translateX(4px);
      }
    }

    &:active {
      transform: translateY(0) scale(0.98);
    }
  }

  /* Right section: Mobile as horizontal scrolling or wrapped grid */
  .right-section {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    padding: clamp(20px, 5vh, 40px) 20px;
    gap: clamp(16px, 3vh, 24px);
    flex-wrap: wrap;
    position: relative;
    width: calc(100% - 80px);
  }

  .stat {
    text-align: center;
    color: white;
    flex: 1;
    min-width: 100px;
    max-width: 200px;
  }

  .stat-value {
    font-size: clamp(24px, min(7vw, 7vh), 72px);
    font-weight: 300;
    margin-bottom: 4px;
    line-height: 1;
  }

  .stat-label {
    font-size: clamp(9px, min(1.8vw, 1.8vh), 12px);
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: 0.8;
    white-space: nowrap;
  }

  /* Tablet adjustments */
  @media (min-width: ${breakpoints.small}) {
    .left-section {
      padding: 40px;
    }

    .right-section {
      padding: 60px 40px;
      gap: 40px;
    }
  }


  @media (max-width: ${breakpoints.medium}) {
    .right-section{
      flex-direction: column;
      width: calc(100% - 40px);
    }
  }
  /* Desktop: Original side-by-side layout */
  @media (min-width: ${breakpoints.large}) {
    flex-direction: row;

    .left-section {
      padding: 60px;
      width: auto;
    }

    .subtitle {
      width: 70%;
      max-width: none;
    }

    .right-section {
      width: 380px;
      border-left: 1px solid rgba(255, 255, 255, 0.3);
      border-top: none;
      flex-direction: column;
      justify-content: center;
      gap: clamp(20px, 6vh, 60px);
      padding: clamp(24px, 6vh, 60px) 40px;
      flex-wrap: nowrap;
    }

    .stat {
      flex: none;
      min-width: auto;
      max-width: none;
    }
  }

  /* Large screens */
  @media (min-width: 1280px) {
    .left-section {
      padding: 80px;
    }

    .right-section {
      width: 400px;
      padding: 60px;
    }
  }

  /* Small phones (iPhone SE, etc) */
  @media (max-width: ${breakpoints.xsmall}) {
    .left-section {
      padding: 20px 16px;
    }

    .cta-button {
      width: 100%;
      justify-content: center;
      padding: clamp(10px, 2vh, 14px) 24px;
    }

    .right-section {
      padding: 32px 16px;
      gap: 24px;
    }

    .stat {
      min-width: 80px;
    }
  }

  /* Landscape mode on mobile - ensure it doesn't break */
  @media (max-height: 600px) and (orientation: landscape) {
    .left-section {
      padding: 20px;
    }

    .main-content {
      padding: 12px 0;
    }

    .right-section {
      flex-direction: row;
      flex-wrap: wrap;
      gap: clamp(16px, 4vw, 40px);
      padding: 16px 20px;
    }
  }
`;

const Comp = ({ stats = [], title='', subtitle='', bg='/verify.png' }) => {
  const navigate = useNavigate()
  return (
    <StyledComp $bgImage={bg}>
      <div className="overlay"></div>
      <div className="bg-img"></div>

      <div className="left-section">
        <div className="main-content">
          <h2 className="title">{title}</h2>

          <div className="prompt">
            <span>&gt;</span>
            <span className="cursor"></span>
          </div>

          <p className="subtitle">{subtitle}</p>

          <button
            className="cta-button"
            onClick={() => { navigate('/login') }}
            aria-label="Get started"
          >
            Let's Get Started
            <ArrowRight size={20} />
          </button>
        </div>
      </div>

      <div className="right-section">
        {stats.map((stat, index) => (
          <div className="stat" key={index}>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </StyledComp>
  );
};

export default Comp;
